import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ColorPickerInput from 'components/color-picker/ColorPicker';
import CustomInput from 'components/custom-input.tsx/CustomInput';
import { selectCurrentTeam } from 'containers/app/appSlice';
import AssignableMembersModal from 'containers/jobs/create-edit-job/components/AssignableMembersModal';
import { useLazyGetAssignableMembersQuery } from 'containers/jobs/api';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { useAppSelector } from 'hooks';
import AssignTeamMembers from '../../components/AssignTeamMembers';
import { SERVICE_AREA_COLORS, ServiceAreaType } from '../constants';
import OptimizedMap from './OptimizedMap';
import { CircleDrawIcon, HandIcon, PolygonDrawIcon } from 'assets/icons';
import { calculatePolygonCentroid } from '../util';
import CustomSwitch from 'components/switch/Switch';

interface FormErrors {
  name?: string;
  shape?: string;
}

interface ExistingShape {
  type: ServiceAreaType;
  vertices?: [number, number][];
  centroid?: [number, number];
  range?: number;
  color: string;
}

interface ServiceAreaFormProps {
  name: string;
  color: string;
  selectedMembers: AssignableMember[];
  onNameChange: (name: string) => void;
  onColorChange: (color: string) => void;
  onMembersChange: (members: AssignableMember[]) => void;
  onShapeComplete?: (shapeData: {
    type: ServiceAreaType;
    data: number[][] | { centroid: [number, number]; range: number };
  }) => void;
  isLoading?: boolean;
  initialShapeData?: {
    type: ServiceAreaType;
    vertices?: [number, number][];
    centroid?: [number, number];
    range?: number;
  };
  errors?: FormErrors;
  onAssignMembersOpen?: () => void;
  onAssignMembersClose?: () => void;
  existingShapes?: ExistingShape[];
}

const ServiceAreaForm: FC<ServiceAreaFormProps> = ({
  name,
  color,
  selectedMembers,
  onNameChange,
  onColorChange,
  onMembersChange,
  onShapeComplete,
  isLoading = false,
  initialShapeData,
  errors = {},
  onAssignMembersOpen,
  onAssignMembersClose,
  existingShapes = []
}) => {
  const [showAssignMembersModal, setShowAssignMembersModal] = useState(false);
  const [assigneeList, setAssigneeList] = useState<AssignableMember[]>([]);
  const [suggestionList, setSuggestionList] = useState<AssignableMember[]>([]);
  const [showExistingShapes, setShowExistingShapes] = useState(true);

  // Calculate initial map center and zoom based on initialShapeData
  const getInitialMapSettings = useCallback(() => {
    if (!initialShapeData) {
      // Default fallback to San Francisco with a wide view
      return { center: { lat: 36.95, lng: -95.86 }, zoom: 4 };
    }

    if (initialShapeData.type === ServiceAreaType.CIRCLE && initialShapeData.centroid && initialShapeData.range) {
      // For circles, calculate zoom based on radius
      // The formula below is approximate:
      // zoom = 14 - log2(radius in meters / 1000)
      const radiusInKm = initialShapeData.range / 1000;
      const zoom = Math.max(1, Math.min(20, Math.round(14 - Math.log2(radiusInKm)))) || 4;
      return {
        center: { lat: initialShapeData.centroid[0], lng: initialShapeData.centroid[1] },
        zoom
      };
    }

    if (initialShapeData.type === ServiceAreaType.POLYGON && initialShapeData.vertices?.length) {
      let minLat = Infinity;
      let maxLat = -Infinity;
      let minLng = Infinity;
      let maxLng = -Infinity;

      initialShapeData.vertices.forEach(([lat, lng]) => {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      });

      // Use provided centroid if available, otherwise calculate the actual centroid
      const center = initialShapeData.centroid
        ? { lat: initialShapeData.centroid[0], lng: initialShapeData.centroid[1] }
        : calculatePolygonCentroid(initialShapeData.vertices);

      // Calculate zoom based on the size of the polygon
      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);
      // Approximate formula: zoom = 14 - log2(maxDiff * 111)
      // (111 km is roughly 1 degree at the equator)
      const zoom = Math.max(1, Math.min(20, Math.round(14 - Math.log2(maxDiff * 111))));

      return { center, zoom };
    }

    // If we have a centroid but no other data, use a default medium zoom
    if (initialShapeData.centroid) {
      return {
        center: { lat: initialShapeData.centroid[0], lng: initialShapeData.centroid[1] },
        zoom: 14
      };
    }

    // Default fallback
    return { center: { lat: 36.95, lng: -95.86 }, zoom: 4 };
  }, [initialShapeData]);

  // Memoize map settings to prevent recalculation on every render
  const mapSettings = useMemo(() => {
    return getInitialMapSettings();
  }, [initialShapeData]); // Only recalculate when initialShapeData changes

  const { center: mapCenter, zoom } = mapSettings;

  const currentTeam = useAppSelector(selectCurrentTeam);
  const [getAssignableTeamMemberQuery, { data: assignableTeamMembers, isSuccess: assignableTeamMembersSuccess }] =
    useLazyGetAssignableMembersQuery();

  useEffect(() => {
    if (currentTeam?.id) {
      getAssignableTeamMemberQuery(currentTeam.id.toString());
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    if (assignableTeamMembersSuccess && assignableTeamMembers?.data?.teamMemberList) {
      setSuggestionList(assignableTeamMembers.data.teamMemberList);
      const selectedMemberIds = selectedMembers?.map(member => member.merchantId) || [];
      setAssigneeList(
        assignableTeamMembers.data.teamMemberList.filter(member => selectedMemberIds.includes(member.merchantId))
      );
    }
  }, [assignableTeamMembersSuccess, assignableTeamMembers, selectedMembers]);

  const handleOpenModal = () => {
    setShowAssignMembersModal(true);
    onAssignMembersOpen?.();
  };

  const handleCloseModal = () => {
    setShowAssignMembersModal(false);
    onAssignMembersClose?.();
  };

  const handleSaveMembers = (members: AssignableMember[]) => {
    onMembersChange(members);
    handleCloseModal();
  };

  // Memoize overlay handlers to prevent unnecessary re-renders
  const memoizedOverlayComplete = useCallback(
    (overlay: google.maps.Polygon | google.maps.Circle) => {
      if (!onShapeComplete) return;

      if (overlay instanceof google.maps.Polygon) {
        const path = overlay.getPath();
        const vertices = path.getArray().map(point => [point.lat(), point.lng()]);
        onShapeComplete({
          type: ServiceAreaType.POLYGON,
          data: vertices
        });
      } else if (overlay instanceof google.maps.Circle) {
        const center = overlay.getCenter();
        if (!center) return;
        const radius = overlay.getRadius();
        onShapeComplete({
          type: ServiceAreaType.CIRCLE,
          data: {
            centroid: [center.lat(), center.lng()],
            range: radius
          }
        });
      }
    },
    [onShapeComplete]
  );

  const memoizedOverlayClear = useCallback(() => {
    if (onShapeComplete) {
      onShapeComplete(null);
    }
  }, [onShapeComplete]);

  return (
    <div className="w-full">
      <div className="flex w-full gap-8 rounded-md border border-borderGray px-8 py-6">
        <div className="w-1/2 space-y-8">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-17px font-semibold leading-[18px] tracking-[-0.3px] text-headingGray">
                Name<span className="pl-0.5 text-error">*</span>
              </div>
            </div>
            <CustomInput
              id="service-area-name"
              value={name}
              onChange={value => {
                onNameChange(value);
              }}
              placeholder="Enter name"
              className={`
                h-12 w-full rounded-md border
                ${errors.name ? 'border-error' : 'border-borderGray'}
                bg-secondaryBtn px-4
              `}
            />
            {errors.name && <div className="text-sbase font-semibold text-error">{errors.name}</div>}
          </div>
          <div>
            <ColorPickerInput
              color={color}
              onChange={onColorChange}
              label="Color code"
              showPresetColors
              showTick
              showSelectedColor
              inputClassName="w-40"
              presetColors={SERVICE_AREA_COLORS}
              customPresetColorsClassName="xl:grid xl:grid-cols-12"
            />
          </div>

          <div className="space-y-2">
            <div className="text-17px font-semibold leading-[18px] tracking-[-0.3px] text-headingGray">
              Assign team members
            </div>
            <AssignTeamMembers
              onMembersChange={onMembersChange}
              membersList={suggestionList}
              initialSelectedMembers={selectedMembers}
              onOpenModal={handleOpenModal}
              isLoading={isLoading}
            />
          </div>
        </div>

        <div className="w-1/2">
          <div className="flex items-center justify-between pb-2">
            <div className="text-17px font-bold text-headingGray">Draw your area</div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-headingGray">Show all service areas</span>
              <CustomSwitch
                enabled={showExistingShapes}
                toggleSwitch={() => setShowExistingShapes(!showExistingShapes)}
                id="show-all-sas"
                className="shrink-0"
              />
            </div>
          </div>
          <div className="mb-2 flex items-center gap-1">
            Pan using{' '}
            <span className="rounded-[4px] border border-borderGray p-1">
              <HandIcon className="h-3 w-3" />
            </span>{' '}
            and mark area with shape{' '}
            <span className="rounded-[4px] border border-borderGray p-1">
              <PolygonDrawIcon className="h-3 w-3" />
            </span>{' '}
            or circle tool{' '}
            <span className="rounded-[4px] border border-borderGray p-1">
              <CircleDrawIcon className="h-3 w-3" />
            </span>
          </div>

          <OptimizedMap
            mapCenter={mapCenter}
            zoom={zoom}
            color={color}
            onOverlayComplete={memoizedOverlayComplete}
            initialShapeData={initialShapeData}
            onOverlayClear={memoizedOverlayClear}
            errors={errors}
            existingShapes={existingShapes}
            showExistingShapes={showExistingShapes}
            onToggleExistingShapes={setShowExistingShapes}
          />
        </div>

        {showAssignMembersModal && (
          <AssignableMembersModal
            list={suggestionList}
            selectedMembers={assigneeList}
            onClose={handleCloseModal}
            onSave={handleSaveMembers}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default ServiceAreaForm;
